import React from "react";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Info } from "lucide-react";
import { EntitySingleSelect } from "@/components/EntitySingleSelect/EntitySingleSelect";
import { EntityMultiSelect } from "@/components/EntityMultiSelect/EntityMultiSelect";
import ThumbnailDisplay from "@/modules/hub/features/FileManagement/components/ThumbnailDisplay/ThumbnailDisplay";
import FilePickerDialog from "@/modules/hub/features/FileManagement/components/FilePickerDialog/FilePickerDialog";
import { formatPhoneNumber } from "@/utils/formatPhoneNumber";
import {
  employeeDisplayName,
  NamedOption,
  UseEmployeeProfileFormPageReturn,
} from "@/modules/administration/features/EmployeeDirectoryManagement/hooks/useEmployeeProfileFormPage";
import { fetchEmployees } from "@/modules/administration/features/EmployeeDirectoryManagement/api/Employee/fetchEmployees/fetchEmployeesApi";
import { fetchSpecialties } from "@/modules/administration/features/EmployeeDirectoryManagement/api/Specialty/fetchSpecialties/fetchSpecialtiesApi";
import { fetchTeams } from "@/modules/administration/features/EmployeeDirectoryManagement/api/Team/fetchTeams/fetchTeamsApi";

interface EmployeeProfileFormProps {
  pageState: UseEmployeeProfileFormPageReturn;
}

const EmployeeProfileForm: React.FC<EmployeeProfileFormProps> = ({
  pageState,
}) => {
  const {
    form,
    mode,
    submitting,
    employeeName,
    isYoutubeUrlEnabled,
    photoDisplayUrl,
    photoFileName,
    isPhotoPickerOpen,
    openPhotoPicker,
    closePhotoPicker,
    handlePhotoSelected,
    handleRemovePhoto,
    onSubmit,
    onCancel,
  } = pageState;

  return (
    <Form {...form}>
      <form
        className="space-y-4 max-w-2xl"
        onSubmit={form.handleSubmit(onSubmit)}
      >
        {mode === "create" && (
          <FormField
            control={form.control}
            name="employee"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Employee</FormLabel>
                <FormControl>
                  <div className="[&_span]:!text-gray-700 [&_span]:dark:!text-slate-300">
                    <EntitySingleSelect<NamedOption>
                      entityNamePlural="Employees"
                      entityNameSingular="Employee"
                      fetchEntities={async ({ searchTerm, page, pageSize }) => {
                        const response = await fetchEmployees({
                          searchTerm,
                          page,
                          pageSize,
                        });
                        const totalCount =
                          response.meta?.totalCount ?? response.data.length;
                        return {
                          data: response.data.map((employee) => ({
                            id: employee.uuid,
                            name: employeeDisplayName(employee),
                          })),
                          totalCount,
                        };
                      }}
                      onChange={field.onChange}
                      placeholder="Select an employee"
                      value={field.value ?? null}
                    />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {mode === "edit" && employeeName && (
          <FormItem>
            <FormLabel>Employee</FormLabel>
            <FormControl>
              <div className="inline-flex h-9 w-full cursor-not-allowed items-center rounded-md border border-gray-300 bg-gray-200 px-3 py-2 text-sm font-medium text-gray-700 shadow-sm dark:border-slate-600 dark:bg-slate-700 dark:text-slate-300">
                {employeeName}
              </div>
            </FormControl>
          </FormItem>
        )}

        <FormField
          control={form.control}
          name="specialties"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Specialties</FormLabel>
              <FormControl>
                <div className="[&_button]:!border-slate-200 [&_button]:dark:!border-slate-700">
                  <EntityMultiSelect<NamedOption>
                    entityNamePlural="Specialties"
                    entityNameSingular="Specialty"
                    fetchEntities={async ({ searchTerm, page, pageSize }) => {
                      const response = await fetchSpecialties({
                        searchTerm,
                        page,
                        pageSize,
                      });
                      const totalCount =
                        response.meta?.totalCount ?? response.data.length;
                      return {
                        data: response.data.map((s) => ({
                          id: s.uuid,
                          name: s.name,
                        })),
                        totalCount,
                      };
                    }}
                    isFullWidth
                    onChange={field.onChange}
                    value={field.value ?? []}
                  />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="teams"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Teams</FormLabel>
              <FormControl>
                <div className="[&_button]:!border-slate-200 [&_button]:dark:!border-slate-700">
                  <EntityMultiSelect<NamedOption>
                    entityNamePlural="Teams"
                    entityNameSingular="Team"
                    fetchEntities={async ({ searchTerm, page, pageSize }) => {
                      const response = await fetchTeams({
                        searchTerm,
                        page,
                        pageSize,
                      });
                      const totalCount =
                        response.meta?.totalCount ?? response.data.length;
                      return {
                        data: response.data.map((t) => ({
                          id: t.uuid,
                          name: t.name,
                        })),
                        totalCount,
                      };
                    }}
                    isFullWidth
                    onChange={field.onChange}
                    value={field.value ?? []}
                  />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="role"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Role</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder="e.g. Director of Coaching"
                  value={field.value ?? ""}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder="name@example.com"
                  type="email"
                  value={field.value ?? ""}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="primaryPhoneNumber"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Primary Phone Number</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  onChange={(e) =>
                    field.onChange(formatPhoneNumber(e.target.value))
                  }
                  placeholder="(555) 555-1234"
                  value={field.value ?? ""}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="bookCallUrl"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Book a Call URL</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder="https://"
                  value={field.value ?? ""}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="linkedInUrl"
          render={({ field }) => (
            <FormItem>
              <FormLabel>LinkedIn URL</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder="https://linkedin.com/in/..."
                  value={field.value ?? ""}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="isYoutubeUrlEnabled"
          render={({ field }) => (
            <FormItem className="flex items-center justify-between rounded-lg border bg-white dark:bg-slate-900 dark:border-slate-700 p-4">
              <div className="space-y-0.5">
                <FormLabel className="text-base">YouTube Video</FormLabel>
              </div>
              <FormControl>
                <Switch
                  checked={Boolean(field.value)}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="youtubeUrl"
          render={({ field }) => (
            <FormItem>
              <div className="flex items-center gap-2">
                <FormLabel>YouTube URL</FormLabel>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger type="button">
                      <Info className="h-4 w-4 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 shadow-lg">
                      <p>
                        Leave blank to show a &ldquo;video coming soon&rdquo;
                        placeholder on the profile until your video is ready.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <FormControl>
                <Input
                  {...field}
                  className="disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400 disabled:dark:bg-slate-800 disabled:dark:text-slate-500"
                  disabled={!isYoutubeUrlEnabled}
                  placeholder="https://youtube.com/@..."
                  value={field.value ?? ""}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="bio"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Bio</FormLabel>
              <FormControl>
                <Textarea {...field} rows={5} value={field.value ?? ""} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="photoUuid"
          render={() => (
            <FormItem>
              <FormLabel>Photo</FormLabel>
              <FormControl>
                <div className="[&>div]:max-w-none [&>div>button]:bg-white [&>div>button]:dark:bg-slate-800">
                  <ThumbnailDisplay
                    onRemove={handleRemovePhoto}
                    onSelect={openPhotoPicker}
                    thumbnailFileName={photoFileName}
                    thumbnailUrl={photoDisplayUrl || null}
                  />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="isPublished"
          render={({ field }) => (
            <FormItem className="flex items-center justify-between rounded-lg border bg-white dark:bg-slate-900 dark:border-slate-700 p-4">
              <div className="space-y-0.5">
                <FormLabel className="text-base">Published</FormLabel>
              </div>
              <FormControl>
                <Switch
                  checked={Boolean(field.value)}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <div className="flex justify-end gap-2">
          <Button onClick={onCancel} type="button" variant="outline">
            Cancel
          </Button>
          <Button disabled={submitting} type="submit">
            {submitting
              ? "Saving..."
              : mode === "create"
                ? "Create"
                : "Save Changes"}
          </Button>
        </div>
      </form>

      <FilePickerDialog
        allowedFileTypes={[
          "image/jpeg",
          "image/png",
          "image/gif",
          "image/webp",
        ]}
        isOpen={isPhotoPickerOpen}
        multiSelect={false}
        namespace="employee_directory"
        onClose={closePhotoPicker}
        onSelect={handlePhotoSelected}
        title="Select Photo"
      />
    </Form>
  );
};

export default EmployeeProfileForm;
